import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { CLAIM_STATUS, type ClaimCodeCheckDto, type ClaimSendReadyDto, type CommunicationDto, type ClaimLineDto, type InsuranceOverviewCountsDto, type IntegrationCallOutcomeDto } from '@his/shared';
import { visibleInterval } from '@/lib/polling';
import { get, getCursorPage, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { cn, dateOnly, humanize, money } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Field, Input, Select, StatusBadge, statusLabel, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ExternalSendButton } from '../integrations/ExternalSendButton';
import { NphiesStatusBadge } from '../integrations/ExternalStatusBadge';
import { describeIntegrationError, reportOutcome } from '../integrations/external-outcome';
import { CommunicationsTab } from './CommunicationsTab';
import { InboundApprovalsPanel } from './InboundApprovalsPanel';
import { PayerInvoicesPanel, usePayerInvoicesRequired } from './PayerInvoicesPanel';
import { PollPanel, usePollStatus } from './PollPanel';
import { BranchPayersPanel } from './flow/BranchPayersPanel';
import { EligibilitySettings } from './flow/EligibilitySettings';
import { EligibilityTab } from './flow/EligibilityTab';
import { MessagesButton } from './flow/MessagesDrawer';
import { PreAuthActions, PreAuthPanel } from './PreAuthPanel';
import { InsuranceModePanel } from './InsuranceModePanel';
import { RemittancesTab } from './RemittancesTab';
import { ClaimExportButton, PayerStatementDialog, RevenueCycleTab, UnclaimedList } from './RcmPanels';
import { ClaimSettlementPanel, ReadyToCloseList } from './ClaimSettlementPanel';
import { ReconciliationsTab, ReconciliationTable } from './ReconciliationsTab';
import { AdjudicationBadge, ExchangeBadge } from './insurance-status';
import { insuranceTabPath, withReturnTo } from './insurance-routes';
import type { Payer } from './PayerFormPage';
import { minorFromInput } from '@/components/MoneyInput';

const SETTLING_STATUSES = ['paid', 'partially_paid', 'rejected'];

interface Claim {
  id: string;
  number: string;
  status: string;
  exchangeState?: string;
  preauthNo: string | null;
  claimedAmountMinor: number;
  approvedAmountMinor: number;
  paidAmountMinor: number;
  submittedAt: string | null;
  ageDays: number | null;
  overdue: boolean;
  payer: { id: string; name: string; claimTatDays: number };
  patient: { id: string; mrn: string; fullName: string } | null;
  items: { id: string; invoiceId: string; amountMinor: number }[];
}

interface ClaimsSummary {
  openCount: number;
  outstandingMinor: number;
  pastTatCount: number;
  rejectedCount: number;
}

const CLAIMS_PAGE_SIZE = 50;

interface AgingRow {
  payerId: string;
  payerName: string;
  claimTatDays: number;
  current: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  byService?: { current: number; days31to60: number; days61to90: number; over90: number };
  outstandingMinor: number;
  overdueCount: number;
  claimCount: number;
}

/**
 * Insurance, panels and TPAs (SRS 9.4).
 *
 * The claims list leads because chasing money already earned is the job; the
 * payer master and the aging report are the two things that make chasing it
 * possible.
 */
export function InsurancePage() {
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') ?? 'claims');
  const nphies = hasModule('nphies');
  const { enabled: pollEnabled, query: poll } = usePollStatus(tab !== 'polling');
  const openRequests = poll.data?.communications.open ?? 0;
  const overview = useQuery({
    queryKey: ['insurance-overview'],
    queryFn: () => get<InsuranceOverviewCountsDto>('/insurance/overview-counts'),
    enabled: can('claim.read', 'preauth.manage'),
    refetchInterval: visibleInterval(60_000),
  });
  const showPreAuths = nphies || Boolean(overview.data?.preauthTabVisible);
  const payerInvoicesRequired = usePayerInvoicesRequired();
  const showEligibility = nphies && can('nphies.eligibility', 'coverage.verify', 'claim.read');
  const showSettings = nphies && can('nphies.settings', 'integration.manage');

  return (
    <>
      <PageHeader
        title={tr('billing', 'ins_page_title')}
        description={tr('billing', 'ins_page_description')}
        actions={
          tab === 'claims' && can('claim.manage') ? (
            <>
              <Button variant="outline" onClick={() => navigate(withReturnTo('/insurance/claims/periods/new', insuranceTabPath('claims')))}>{tr('billing', 'ins_claim_a_period')}</Button>
              <Button onClick={() => navigate(withReturnTo('/insurance/claims/new', insuranceTabPath('claims')))}>{tr('billing', 'ins_assemble_a_claim')}</Button>
            </>
          ) : tab === 'preauth' && showPreAuths ? (
            <PreAuthActions />
          ) : tab === 'payments' && can('remittance.post') ? (
            <Button onClick={() => navigate(withReturnTo('/insurance/remittances/new', insuranceTabPath('payments')))}>{tr('billing', 'ins_record_a_payment_received')}</Button>
          ) : tab === 'payers' && can('payer.manage') ? (
            <Button onClick={() => navigate(withReturnTo('/insurance/payers/new', insuranceTabPath('payers')))}>{tr('billing', 'ins_add_payer')}</Button>
          ) : null
        }
      />
      {tab !== 'polling' && <PollPanel />}
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          ...(showEligibility ? [{ id: 'eligibility', label: tr('billing', 'nx_tab_eligibility') }] : []),
          { id: 'claims', label: tr('billing', 'nx_claims') },
          { id: 'aging', label: tr('billing', 'ins_tab_aging') },
          ...(can('claim.read') ? [{ id: 'reports', label: tr('billing', 'ins_tab_revenue_cycle') }] : []),
          ...(showPreAuths ? [{ id: 'preauth', label: tr('billing', 'nx_view_preauths'), badge: overview.data && overview.data.pendingPreAuths > 0 ? <Badge variant="secondary">{overview.data.pendingPreAuths}</Badge> : undefined }] : []),
          ...(can('claim.read', 'remittance.post') ? [{ id: 'payments', label: tr('billing', 'payments') }] : []),
          ...(nphies
            ? [
                { id: 'requests', label: tr('billing', 'ins_tab_insurer_requests'), badge: openRequests > 0 ? <Badge variant="warning">{openRequests}</Badge> : undefined },
              ]
            : []),
          ...(pollEnabled ? [{ id: 'polling', label: tr('billing', 'nx_tab_polling') }] : []),
          ...(can('payer.manage') ? [{ id: 'payers', label: tr('billing', 'nx_tab_payers_plans') }] : []),
          ...(showSettings ? [{ id: 'settings', label: tr('billing', 'nx_tab_settings') }] : []),
        ]}
      />
      {tab === 'eligibility' && showEligibility && <EligibilityTab />}
      {tab === 'polling' && nphies && <PollPanel live={false} />}
      {tab === 'settings' && showSettings && <EligibilitySettings />}
      {tab === 'claims' && <ClaimsTab />}
      {tab === 'aging' && <AgingTab />}
      {tab === 'reports' && <RevenueCycleTab />}
      {tab === 'preauth' && showPreAuths && <PreAuthPanel />}
      {tab === 'payments' && (
        <div className="space-y-6">
          <RemittancesTab />
          {nphies && <ReconciliationsTab />}
          {payerInvoicesRequired && can('claim.read', 'payer_invoice.issue') && <PayerInvoicesPanel />}
        </div>
      )}
      {tab === 'requests' && nphies && (
        <>
          <CommunicationsTab />
          {can('nphies.inbound.link', 'preauth.manage') && <InboundApprovalsPanel />}
        </>
      )}
      {tab === 'payers' && (
        <>
          <PayersTab />
          <BranchPayersPanel />
          <InsuranceModePanel />
        </>
      )}
    </>
  );
}

function SendReadyClaimsButton({ branchId }: { branchId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const send = useMutation({
    mutationFn: () => post<ClaimSendReadyDto>('/integrations/nphies/claims/send-ready', branchId ? { branchId } : {}),
    onSuccess: (result) => {
      const note = [
        tr('billing', 'ins_x_claims_queued_deferred', { count: result.queued.length }),
        result.skipped.length > 0 ? tr('billing', 'ins_x_not_ready', { count: result.skipped.length, list: result.skipped.slice(0, 3).map((row) => `${row.number} (${row.reason})`).join('; ') }) : '',
        result.more ? tr('billing', 'ins_more_waiting_press_again') : '',
      ].filter(Boolean).join(' · ');
      if (result.queued.length > 0) toast.success(tr('billing', 'ins_sent_with_monthly_batch'), note);
      else toast.warning(tr('billing', 'ins_nothing_ready_to_send'), note);
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (error) => toast.error(tr('billing', 'ins_could_not_send_ready_claims'), describeIntegrationError(error)),
  });
  return (
    <Button variant="outline" size="sm" loading={send.isPending} onClick={() => send.mutate()}>
      {tr('billing', 'ins_send_all_ready_claims')}
    </Button>
  );
}

function ClaimsTab() {
  const { can, user, hasModule } = useAuth();
  const [status, setStatus] = useState('');
  const workingBranchId = useWorkingBranchId();
  const [branchPick, setBranchPick] = useState<{ under: string; value: string } | null>(null);
  const branchId = branchPick && branchPick.under === workingBranchId ? branchPick.value : workingBranchId;
  const setBranchId = (value: string) => setBranchPick({ under: workingBranchId, value });
  const branches = user?.branches ?? [];
  const [showUnclaimed, setShowUnclaimed] = useState(false);
  const [open, setOpen] = useState<Claim | null>(null);

  const filters = { status: status || undefined, branchId: branchId || undefined };
  const claims = useInfiniteQuery({
    queryKey: ['claims', status, branchId],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      getCursorPage<Claim>(`/insurance/claims${qs({ ...filters, limit: CLAIMS_PAGE_SIZE, cursor: pageParam || undefined })}`, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const summary = useQuery({
    queryKey: ['claims', 'summary', status, branchId],
    queryFn: () => get<ClaimsSummary>(`/insurance/claims/summary${qs(filters)}`),
  });

  const rows = claims.data?.pages.flatMap((page) => page.rows) ?? [];
  const figures = summary.data;

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={tr('billing', 'claims_tile_open')} value={figures ? figures.openCount : '—'} />
        <StatTile label={tr('billing', 'claims_tile_outstanding')} value={figures ? money(figures.outstandingMinor) : '—'} />
        <StatTile
          label={tr('billing', 'claims_tile_past_tat')}
          value={figures ? figures.pastTatCount : '—'}
          tone={figures && figures.pastTatCount > 0 ? 'destructive' : 'default'}
        />
        <StatTile label={tr('billing', 'rejected')} value={figures ? figures.rejectedCount : '—'} tone="warning" />
      </div>

      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('billing', 'status')}>
              <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{tr('billing', 'all_statuses')}</option>
                {CLAIM_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </Select>
            </FilterField>
            {branches.length > 1 && (
              <FilterField label={tr('billing', 'branch')}>
                <Select className="w-48" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{tr('billing', 'every_branch')}</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Select>
              </FilterField>
            )}
          </>
        }
        trailing={
          <>
            {can('claim.read') && <ClaimExportButton />}
            {hasModule('nphies') && can('nphies.claim.send') && <SendReadyClaimsButton branchId={branchId} />}
            <Button size="sm" variant="ghost" onClick={() => setShowUnclaimed(!showUnclaimed)}>{showUnclaimed ? tr('billing', 'ins_hide_bills_not_yet_claimed') : tr('billing', 'ins_bills_not_yet_claimed')}</Button>
          </>
        }
        onReset={
          status || branchId !== workingBranchId
            ? () => {
                setStatus('');
                setBranchPick(null);
              }
            : undefined
        }
      />

      {showUnclaimed && (
        <div className="mb-4">
          <UnclaimedList />
        </div>
      )}

      {can('claim.settle') && (
        <ReadyToCloseList onOpen={(id) => setOpen(rows.find((row) => row.id === id) ?? ({ id, number: '', status: 'partially_paid', preauthNo: null, claimedAmountMinor: 0, approvedAmountMinor: 0, paidAmountMinor: 0, submittedAt: null, ageDays: null, overdue: false, payer: { id: '', name: '', claimTatDays: 0 }, patient: null, items: [] } as Claim))} />
      )}

      <DataTable
        rows={rows}
        loading={claims.isLoading} refreshing={claims.isFetching}
        error={claims.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row)}
        empty={tr('billing', 'no_claims_match')}
        partial={Boolean(claims.hasNextPage)}
        footer={
          claims.hasNextPage ? (
            <Button variant="outline" size="sm" className="ms-auto" onClick={() => void claims.fetchNextPage()} loading={claims.isFetchingNextPage}>
              {tr('billing', 'show_more')}
            </Button>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'claim'), sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
          { header: tr('billing', 'payer'), sort: (row) => row.payer.name, cell: (row) => row.payer.name },
          {
            header: tr('billing', 'patient'),
            sort: (row) => row.patient?.fullName,
            cell: (row) =>
              row.patient ? (
                <div>
                  <p>{row.patient.fullName}</p>
                  <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
                </div>
              ) : (
                '—'
              ),
          },
          { header: tr('billing', 'nx_claimed'), align: 'right', sort: (row) => row.claimedAmountMinor, cell: (row) => money(row.claimedAmountMinor) },
          { header: tr('billing', 'paid'), align: 'right', sort: (row) => row.paidAmountMinor, cell: (row) => money(row.paidAmountMinor) },
          {
            header: tr('billing', 'ins_age'),
            align: 'right',
            sort: (row) => row.ageDays,
            cell: (row) =>
              row.ageDays === null ? (
                <span className="text-muted-foreground">{tr('billing', 'ins_not_sent')}</span>
              ) : (
                <span className={cn('tabular', row.overdue && 'font-semibold text-destructive')}>
                  {tr('billing', 'ins_x_days_short', { days: row.ageDays })}
                </span>
              ),
          },
          {
            header: tr('billing', 'status'),
            sort: (row) => row.status,
            cell: (row) => (
              <span className="flex flex-col items-start gap-1">
                <StatusBadge status={row.status} />
                {row.exchangeState && row.exchangeState !== 'unsent' && row.status !== 'paid' && <ExchangeBadge state={row.exchangeState} />}
              </span>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <span onClick={(event) => event.stopPropagation()}>
                <MessagesButton entityType="claim" entityId={row.id} title={tr('billing', 'nx_messages_for_claim', { number: row.number })} />
              </span>
            ),
          },
        ]}
      />

      {open !== null && (
        <ClaimDialog claim={open} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function ClaimDialog({ claim, onClose }: { claim: Claim | null; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({
    preauthNo: '',
    approvedAmountMinor: '',
    paidAmountMinor: '',
    rejectionReason: '',
    queryNote: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { can, hasModule } = useAuth();
  const nphies = hasModule('nphies');
  const [overrideKeys, setOverrideKeys] = useState<string[]>([]);

  const detail = useQuery({
    queryKey: ['claim', claim?.id],
    queryFn: () =>
      get<
        Claim & {
          allowedTransitions: string[];
          exchangeNote: string | null;
          adjudicationHash: string | null;
          patientShareMinor: number;
          rejectedMinor: number;
          items: { invoice: { number: string; context: string; netMinor: number; creditMinor: number } }[];
          lines: (ClaimLineDto & { requestNo: number; approvedQuantity: number | null })[];
          reconciliations: { id: string }[];
          communications: { id: string; subject: string | null; requestText: string | null; dueAt: string | null; priority: string }[];
        }
      >(`/insurance/claims/${claim?.id}`),
    enabled: Boolean(claim),
  });

  const sendable = Boolean(nphies && detail.data && (detail.data.allowedTransitions.includes('submitted') || ['approved', 'partially_paid'].includes(detail.data.status)));

  const plan = useQuery({
    queryKey: ['claim-send-plan', claim?.id, overrideKeys],
    queryFn: () =>
      get<{
        send: { display: string; quantity: number; netMinor: number }[];
        skipped: { display: string; reason: string; sourceKey: string; overridable: boolean }[];
        unmapped: { description: string }[];
      }>(`/integrations/nphies/claims/${claim?.id}/send-plan${qs({ includeRejectedAuth: overrideKeys.length ? overrideKeys.join(',') : undefined })}`),
    enabled: sendable,
    staleTime: 30_000,
  });

  const codeCheck = useQuery({
    queryKey: ['claim-code-check', claim?.id],
    queryFn: () => get<ClaimCodeCheckDto>(`/integrations/nphies/claims/${claim?.id}/code-check`),
    enabled: sendable,
    staleTime: 30_000,
  });

  const refreshClaim = () => {
    void queryClient.invalidateQueries({ queryKey: ['claims'] });
    void queryClient.invalidateQueries({ queryKey: ['claim', claim?.id] });
    void queryClient.invalidateQueries({ queryKey: ['claim-send-plan', claim?.id] });
    void queryClient.invalidateQueries({ queryKey: ['nphies-status', 'claim', claim?.id] });
  };

  const ask = useMutation({
    mutationFn: (action: 'cancel' | 'status-check') => post<IntegrationCallOutcomeDto>(`/integrations/nphies/claims/${claim?.id}/${action}`, {}),
    onSuccess: (outcome, action) => {
      reportOutcome(toast, outcome, action === 'cancel' ? tr('billing', 'ins_cancellation_sent') : tr('billing', 'ins_the_insurer_answered'));
      refreshClaim();
    },
    onError: (caught) => toast.error(tr('billing', 'could_not_do_that'), describeIntegrationError(caught)),
  });

  const splitPharmacy = useMutation({
    mutationFn: () => post<{ number: string; invoices: number }>(`/insurance/claims/${claim?.id}/split-pharmacy`, {}),
    onSuccess: (split) => {
      toast.success(tr('billing', 'ins_pharmacy_claim_x_drafted', { number: split.number }), tr('billing', 'ins_x_pharmacy_bills_moved', { count: split.invoices }));
      refreshClaim();
    },
    onError: (caught) => toast.error(tr('billing', 'ins_could_not_split_claim'), describeIntegrationError(caught)),
  });
  const openConversation = useMutation({
    mutationFn: (communicationId: string | null) =>
      communicationId
        ? get<CommunicationDto>(`/integrations/nphies/communications/${communicationId}`)
        : post<{ communication: CommunicationDto }>('/integrations/nphies/communications', { aboutKind: 'claim', aboutId: claim?.id, send: false }).then((created) => created.communication),
    onSuccess: (communication) => navigate(withReturnTo(`/insurance/requests/${communication.id}/reply`, insuranceTabPath('claims'))),
    onError: (caught) => toast.error(tr('billing', 'ins_could_not_open_message'), describeIntegrationError(caught)),
  });
  const pharmacyBills = ((detail.data?.items ?? []) as { invoice?: { context: string } }[]).filter((item) => item.invoice?.context === 'pharmacy').length;
  const mayMixPharmacy =
    nphies && can('claim.manage') && detail.data?.status === 'draft' && detail.data.exchangeState !== 'sent' && pharmacyBills > 0 && pharmacyBills < (detail.data?.items.length ?? 0);

  const transition = useMutation({
    mutationFn: () =>
      put(`/insurance/claims/${claim?.id}/status`, {
        status,
        preauthNo: form.preauthNo || undefined,
        approvedAmountMinor: form.approvedAmountMinor ? minorFromInput(form.approvedAmountMinor) : undefined,
        paidAmountMinor: form.paidAmountMinor ? minorFromInput(form.paidAmountMinor) : undefined,
        rejectionReason: form.rejectionReason || undefined,
        queryNote: form.queryNote || undefined,
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'ins_claim_updated'));
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['claim', claim?.id] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const d = detail.data;

  return (
    <Dialog
      open={Boolean(claim)}
      onClose={onClose}
      wide
      title={claim ? tr('billing', 'ins_claim_x', { number: claim.number }) : ''}
      description={claim ? `${claim.payer.name} · ${claim.patient?.fullName ?? ''}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'close')}
          </Button>
          {claim && detail.data && nphies && (
            <>
              {can('nphies.status') && detail.data.exchangeState === 'sent' && ['submitted', 'queried'].includes(detail.data.status) && (
                <Button variant="outline" loading={ask.isPending && ask.variables === 'status-check'} onClick={() => ask.mutate('status-check')}>
                  {tr('billing', 'ins_check_status')}
                </Button>
              )}
              {can('nphies.cancel') && detail.data.allowedTransitions.includes('cancelled') && (
                <Button variant="ghost" loading={ask.isPending && ask.variables === 'cancel'} onClick={() => ask.mutate('cancel')}>
                  {tr('billing', 'ins_cancel_the_claim')}
                </Button>
              )}
            </>
          )}
          {claim && mayMixPharmacy && (
            <Button variant="outline" loading={splitPharmacy.isPending} onClick={() => splitPharmacy.mutate()}>
              {tr('billing', 'ins_split_off_pharmacy_bills')}
            </Button>
          )}
          {claim && sendable && detail.data?.status === 'draft' && (
            <ExternalSendButton
              path={`/integrations/nphies/claims/${claim.id}/send`}
              label={tr('billing', 'ins_send_with_monthly_batch')}
              doneTitle={tr('billing', 'ins_claim_queued_with_monthly_batch')}
              module="nphies"
              permission="nphies.claim.send"
              size="default"
              variant="ghost"
              body={{ includeRejectedAuth: overrideKeys, priority: 'deferred' }}
              disabled={Boolean(codeCheck.data && !codeCheck.data.ready) || Boolean(plan.data && plan.data.send.length === 0)}
              invalidate={[['claims'], ['claim', claim.id], ['claim-send-plan', claim.id], ['nphies-status', 'claim', claim.id]]}
            />
          )}
          {claim && sendable && (
            <ExternalSendButton
              path={`/integrations/nphies/claims/${claim.id}/send`}
              label={detail.data && detail.data.lines.length > 0 ? tr('billing', 'ins_send_the_new_lines') : tr('billing', 'ins_send_to_nphies')}
              doneTitle={tr('billing', 'ins_claim_sent_to_insurer')}
              module="nphies"
              permission="nphies.claim.send"
              size="default"
              body={{ includeRejectedAuth: overrideKeys }}
              disabled={Boolean(codeCheck.data && !codeCheck.data.ready) || Boolean(plan.data && plan.data.send.length === 0)}
              invalidate={[['claims'], ['claim', claim.id], ['claim-send-plan', claim.id], ['nphies-status', 'claim', claim.id]]}
            />
          )}
          {status && (
            <Button
              onClick={() => {
                setError(null);
                transition.mutate();
              }}
              loading={transition.isPending}
            >
              {tr('billing', 'ins_mark_x', { status: statusLabel(status) })}
            </Button>
          )}
        </>
      }
    >
      {d && (
        <div className="space-y-4">
          <NphiesStatusBadge entityType="claim" entityId={d.id} />
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile label={tr('billing', 'nx_claimed')} value={money(d.claimedAmountMinor)} />
            <StatTile label={tr('billing', 'nx_approved')} value={money(d.approvedAmountMinor)} />
            <StatTile label={tr('billing', 'paid')} value={money(d.paidAmountMinor)} tone="success" />
          </div>

          {d.overdue && (
            <Alert variant="warning">
              <span className="inline-flex items-center gap-1 font-semibold">
                <AlertTriangle className="h-4 w-4" /> {tr('billing', 'ins_x_days_with_payer', { count: d.ageDays ?? 0 })}
              </span>{' '}
              {tr('billing', 'ins_past_agreed_turnaround', { days: d.payer.claimTatDays })}
            </Alert>
          )}

          <DataTable
            dense
            rows={d.items}
            rowKey={(row) => row.invoice.number}
            columns={[
              { header: tr('billing', 'invoice'), cell: (row) => row.invoice.number },
              { header: tr('billing', 'context'), cell: (row) => <Badge variant="secondary">{humanize(row.invoice.context, 'invoice_context')}</Badge> },
              { header: tr('billing', 'ins_invoice_total'), align: 'right', cell: (row) => money(row.invoice.netMinor) },
              { header: tr('billing', 'panel_share'), align: 'right', cell: (row) => money(row.invoice.creditMinor) },
            ]}
          />

          {d.exchangeNote && <Alert variant={d.exchangeState === 'failed' ? 'destructive' : 'warning'}>{d.exchangeNote}</Alert>}

          {d.communications.length > 0 && (
            <Alert variant="warning" title={tr('billing', 'ins_insurer_is_asking')}>
              <ul className="mt-1 list-disc ps-5">
                {d.communications.map((item) => (
                  <li key={item.id}>
                    {item.subject ? `${item.subject}: ` : ''}
                    {item.requestText ?? tr('billing', 'ins_no_text')}
                    {item.dueAt ? ` ${tr('billing', 'ins_answer_by_x', { date: dateOnly(item.dueAt) })}` : ''}
                  </li>
                ))}
              </ul>
              {can('nphies.communication') && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.communications.map((item) => (
                    <Button key={item.id} size="sm" variant="outline" loading={openConversation.isPending && openConversation.variables === item.id} onClick={() => openConversation.mutate(item.id)}>
                      {item.subject ? tr('billing', 'ins_answer_x', { subject: item.subject }) : tr('billing', 'ins_answer_the_request')}
                    </Button>
                  ))}
                </div>
              )}
            </Alert>
          )}
          {nphies && can('nphies.communication') && d.exchangeState === 'sent' && (
            <Button size="sm" variant="ghost" loading={openConversation.isPending && openConversation.variables === null} onClick={() => openConversation.mutate(null)}>
              {tr('billing', 'ins_write_to_insurer_about_claim')}
            </Button>
          )}

          {codeCheck.data && !codeCheck.data.ready && (
            <Alert variant="warning" title={tr('billing', 'ins_services_no_insurer_code')}>
              <ul className="mt-1 list-disc ps-5">
                {codeCheck.data.unmapped.slice(0, 8).map((line, index) => (
                  <li key={`${line.invoiceNumber}-${index}`}>{line.description} ({line.invoiceNumber})</li>
                ))}
              </ul>
              <p className="mt-1 text-xs">{tr('billing', 'ins_add_them_in_settings_code_mapping')}</p>
            </Alert>
          )}

          {d.lines.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium">{tr('billing', 'ins_what_insurer_said_line_by_line')}</p>
              <DataTable
                dense
                rows={d.lines}
                rowKey={(line) => line.id}
                columns={[
                  { header: '#', width: '40px', cell: (line) => (line.requestNo > 0 ? `${line.sequence} (r${line.requestNo})` : line.sequence) },
                  { header: tr('billing', 'service'), cell: (line) => <div><p>{line.display}</p><p className="font-mono text-xs text-muted-foreground">{line.code}</p></div> },
                  { header: tr('billing', 'qty'), align: 'right', cell: (line) => (line.approvedQuantity !== null && line.approvedQuantity !== line.quantity ? tr('billing', 'ins_x_of_y', { approved: line.approvedQuantity, quantity: line.quantity }) : line.quantity) },
                  { header: tr('billing', 'nx_claimed'), align: 'right', cell: (line) => money(line.submittedMinor) },
                  { header: tr('billing', 'ins_insurer_pays'), align: 'right', cell: (line) => money(line.approvedMinor) },
                  { header: tr('billing', 'ins_patient_pays'), align: 'right', cell: (line) => money(line.patientShareMinor) },
                  { header: tr('billing', 'ins_answer'), cell: (line) => <AdjudicationBadge status={line.adjudicationStatus} /> },
                  { header: tr('billing', 'why'), cell: (line) => <span className="text-xs text-muted-foreground">{line.reasonCode && <span className="me-1 font-mono">{line.reasonCode}</span>}{line.reasonText}</span> },
                ]}
              />
            </div>
          )}

          {sendable && plan.data && (
            <div className="space-y-2 rounded-md border p-3 text-sm">
              <p className="font-medium">
                {plan.data.send.length > 0
                  ? tr('billing', 'ins_send_now_carries_x_lines', { count: plan.data.send.length })
                  : tr('billing', 'ins_nothing_new_to_send')}
              </p>
              {plan.data.send.length > 0 && (
                <ul className="list-disc ps-5 text-muted-foreground">
                  {plan.data.send.map((line, index) => (
                    <li key={`${line.display}-${index}`}>
                      {line.display} × {line.quantity} · {money(line.netMinor)}
                    </li>
                  ))}
                </ul>
              )}
              {plan.data.skipped.length > 0 && (
                <div>
                  <p className="font-medium">{tr('billing', 'ins_left_out')}</p>
                  <ul className="space-y-1 ps-1">
                    {plan.data.skipped.map((line) => (
                      <li key={line.sourceKey} className="flex flex-wrap items-center gap-2 text-muted-foreground">
                        <span>
                          {line.display}: {line.reason}
                        </span>
                        {line.overridable && can('nphies.preauth.resend', 'preauth.resend') && (
                          <Checkbox
                            checked={overrideKeys.includes(line.sourceKey)}
                            onCheckedChange={(checked) => setOverrideKeys(checked ? [...overrideKeys, line.sourceKey] : overrideKeys.filter((key) => key !== line.sourceKey))}
                            label={tr('billing', 'ins_send_it_anyway')}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!['draft', 'cancelled'].includes(d.status) && <ClaimSettlementPanel claimId={d.id} onChanged={refreshClaim} />}

          {nphies && d.reconciliations.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium">{tr('billing', 'ins_payments_insurer_reported')}</p>
              <ReconciliationTable claimId={d.id} />
            </div>
          )}

          <Field label={tr('billing', 'ins_next_step')} hint={d.allowedTransitions.length === 0 ? tr('billing', 'ins_claim_is_closed') : d.adjudicationHash ? tr('billing', 'ins_insurer_answered_over_nphies') : undefined}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{tr('billing', 'select')}</option>
              {d.allowedTransitions.filter((t) => !(d.adjudicationHash && (t === 'approved' || t === 'rejected')) && (can('claim.settle') || !SETTLING_STATUSES.includes(t))).map((t) => (
                <option key={t} value={t}>
                  {statusLabel(t)}
                </option>
              ))}
            </Select>
          </Field>

          {status === 'submitted' && (
            <Field label={tr('billing', 'ins_preauth_number')} hint={tr('billing', 'ins_required_by_payers_preauth')}>
              <Input value={form.preauthNo} onChange={(e) => setForm({ ...form, preauthNo: e.target.value })} />
            </Field>
          )}
          {status === 'queried' && (
            <Field label={tr('billing', 'ins_what_did_payer_ask')} required>
              <Textarea rows={2} value={form.queryNote} onChange={(e) => setForm({ ...form, queryNote: e.target.value })} />
            </Field>
          )}
          {status === 'approved' && (
            <Field label={tr('billing', 'ins_approved_amount')} required>
              <Input
                type="text" dir="ltr" inputMode="decimal"
                value={form.approvedAmountMinor}
                onChange={(e) => setForm({ ...form, approvedAmountMinor: e.target.value })}
              />
            </Field>
          )}
          {(status === 'paid' || status === 'partially_paid') && (
            <Field label={tr('billing', 'ins_amount_received')} required>
              <Input
                type="text" dir="ltr" inputMode="decimal"
                value={form.paidAmountMinor}
                onChange={(e) => setForm({ ...form, paidAmountMinor: e.target.value })}
              />
            </Field>
          )}
          {status === 'rejected' && (
            <Field label={tr('billing', 'ins_rejection_reason')} required>
              <Textarea
                rows={2}
                value={form.rejectionReason}
                onChange={(e) => setForm({ ...form, rejectionReason: e.target.value })}
              />
            </Field>
          )}

          {error && <Alert variant="destructive">{error}</Alert>}
        </div>
      )}
    </Dialog>
  );
}

function AgingTab() {
  const aging = useQuery({ queryKey: ['claim-aging'], queryFn: () => get<AgingRow[]>('/insurance/reports/aging') });
  const [clock, setClock] = useState<'submission' | 'service'>('submission');
  const rows = (aging.data ?? []).map((row) => (clock === 'service' && row.byService ? { ...row, ...row.byService } : row));

  return (
    <>
    <SearchFilterBar
      filters={
        <FilterField label={tr('billing', 'age_measured_from')}>
          <Select className="w-56" value={clock} onChange={(e) => setClock(e.target.value as 'submission' | 'service')}>
            <option value="submission">{tr('billing', 'ins_age_from_sent')}</option>
            <option value="service">{tr('billing', 'ins_age_from_service_date')}</option>
          </Select>
        </FilterField>
      }
      onReset={clock !== 'submission' ? () => setClock('submission') : undefined}
    />
    <DataTable
      rows={rows}
      loading={aging.isLoading} refreshing={aging.isFetching}
      error={aging.error}
      rowKey={(row) => row.payerId}
      empty={tr('billing', 'ins_nothing_outstanding_any_payer')}
      columns={[
        { header: tr('billing', 'payer'), sort: (row) => row.payerName, cell: (row) => row.payerName },
        { header: tr('billing', 'nx_claims'), align: 'right', sort: (row) => row.claimCount, cell: (row) => row.claimCount },
        { header: tr('billing', 'ins_0_30_days'), align: 'right', sort: (row) => row.current, cell: (row) => money(row.current) },
        { header: '31–60', align: 'right', sort: (row) => row.days31to60, cell: (row) => money(row.days31to60) },
        { header: '61–90', align: 'right', sort: (row) => row.days61to90, cell: (row) => money(row.days61to90) },
        {
          header: tr('billing', 'ins_over_90'),
          align: 'right',
          sort: (row) => row.over90,
          cell: (row) => <span className={row.over90 > 0 ? 'font-semibold text-destructive' : ''}>{money(row.over90)}</span>,
        },
        { header: tr('billing', 'total'), align: 'right', sort: (row) => row.outstandingMinor, cell: (row) => <span className="font-medium">{money(row.outstandingMinor)}</span> },
        {
          header: tr('billing', 'ins_past_tat'),
          align: 'right',
          sort: (row) => row.overdueCount,
          cell: (row) =>
            row.overdueCount > 0 ? <Badge variant="destructive">{row.overdueCount}</Badge> : <span>—</span>,
        },
      ]}
    />
    </>
  );
}

function PayersTab() {
  const navigate = useNavigate();
  const [statementOf, setStatementOf] = useState<Payer | null>(null);
  const returnTo = insuranceTabPath('payers');

  const payers = useQuery({
    queryKey: ['payers', 'all'],
    queryFn: () => get<Payer[]>('/insurance/payers?includeInactive=true'),
  });

  const manage = (payer: Payer, tab: 'plans' | 'rules' | 'service-codes') => navigate(withReturnTo(`/insurance/payers/${payer.id}?tab=${tab}`, returnTo));

  return (
    <>
      <DataTable
        rows={payers.data}
        loading={payers.isLoading} refreshing={payers.isFetching}
        error={payers.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(withReturnTo(`/insurance/payers/${row.id}/edit`, returnTo))}
        empty={tr('billing', 'ins_no_payers_on_file')}
        columns={[
          { header: tr('billing', 'code'), sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
          { header: tr('billing', 'name'), sort: (row) => row.name, cell: (row) => row.name },
          { header: tr('billing', 'kind'), sort: (row) => row.kind, cell: (row) => <Badge variant="secondary">{statusLabel(row.kind)}</Badge> },
          { header: tr('billing', 'ins_co_pay'), align: 'right', sort: (row) => row.coPayPct, cell: (row) => `${row.coPayPct}%` },
          { header: tr('billing', 'ins_pre_auth'), sort: (row) => row.requiresPreAuth, cell: (row) => (row.requiresPreAuth ? tr('billing', 'ins_required') : tr('billing', 'ins_not_required')) },
          { header: tr('billing', 'ins_tat'), align: 'right', sort: (row) => row.claimTatDays, cell: (row) => tr('billing', 'ins_x_days_short', { days: row.claimTatDays }) },
          { header: tr('billing', 'active'), sort: (row) => row.isActive, cell: (row) => (row.isActive ? <Badge variant="success">{tr('billing', 'ins_yes')}</Badge> : <Badge variant="muted">{tr('billing', 'ins_no')}</Badge>) },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button
                size="sm"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  manage(row, 'plans');
                }}
              >
                {tr('billing', 'plans')}
              </Button>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  setStatementOf(row);
                }}
              >
                {tr('billing', 'statement')}
              </Button>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  manage(row, 'service-codes');
                }}
              >
                {tr('billing', 'service_mapping')}
              </Button>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  manage(row, 'rules');
                }}
              >
                {tr('billing', 'pre_authorisation_rules')}
              </Button>
            ),
          },
        ]}
      />
      {statementOf && <PayerStatementDialog payer={statementOf} onClose={() => setStatementOf(null)} />}
    </>
  );
}
